import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { Briefcase, Home, AlertTriangle, Clock, CalendarClock, Check, CheckCircle2 } from 'lucide-react'

import type { Task } from '../lib/types'
import { formatDeadline, deadlineColor } from '../lib/format'
import { hapticSuccess, hapticImpact } from '../lib/telegram'

interface Props {
  task: Task
  canCheck: boolean
  onToggleDone?: (taskId: number) => void
  detailPath?: string
}

export default function TaskCard({ task, canCheck, onToggleDone, detailPath }: Props) {
  const navigate = useNavigate()
  const [checked, setChecked] = useState(false)

  const isDone = task.status === 'done'
  const color = isDone ? 'green' : deadlineColor(task.deadline)
  const CategoryIcon = task.category === 'work' ? Briefcase : Home

  const pillIcon = isDone
    ? <CheckCircle2 />
    : color === 'red' ? <AlertTriangle />
    : color === 'orange' ? <Clock />
    : <CalendarClock />

  const onCheckClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canCheck || checked || isDone) return
    hapticSuccess()
    setChecked(true)
    setTimeout(() => onToggleDone?.(task.id), 350)
  }

  const goDetail = () => {
    if (detailPath) {
      hapticImpact('light')
      navigate(detailPath)
    }
  }

  return (
    <AnimatePresence>
      {!checked && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, x: 60, transition: { duration: 0.3 } }}
          layout
        >
          <div
            className={`task-card stripe-${color}`}
            onClick={goDetail}
            role="button"
          >
            <button
              type="button"
              className={`task-check${canCheck && !isDone ? ' is-active' : ''}${isDone ? ' is-checked' : ''}`}
              onClick={onCheckClick}
              aria-label={canCheck ? 'Отметить выполненным' : 'Только исполнитель может закрыть'}
            >
              {isDone && <Check size={14} strokeWidth={3} />}
            </button>

            <div className="task-card-body">
              <div className="task-card-title">{task.title}</div>
              <div className="task-card-meta">
                <span className={`deadline-pill deadline-${isDone ? 'done' : color}`}>
                  {pillIcon}
                  {isDone ? 'выполнено' : formatDeadline(task.deadline)}
                </span>
                <span className="task-card-category">
                  <CategoryIcon />
                  {task.category === 'work' ? 'Рабочая' : 'Личная'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
